/**
 * โครงฟอร์มร่วมของหน้าจอหน้างานทั้งสามโหมด
 *
 * มีที่เดียวเพราะสิ่งที่ห้ามพูดผิดต้องถูกเขียนครั้งเดียว: ปุ่มกดแล้วได้
 * **"เข้าคิวแล้ว"** ไม่ใช่ "บันทึกสำเร็จ" และเมื่อคิวในเครื่องใช้งานไม่ได้
 * ต้องบอกก่อนกด ไม่ใช่ปล่อยให้ผู้ปฏิบัติงานสแกนทั้งกะแล้วค่อยรู้ว่าไม่มีอะไร
 * ถูกเก็บไว้เลย (D-075, `docs/SECURITY.md` — Offline/Online Policy)
 *
 * ขนาดของช่องกรอกและปุ่มมาจาก `touch.ts` เป็น inline style เพื่อให้เทสต์วัดได้จริง
 * (jsdom ไม่โหลด stylesheet) — เครื่องนี้ถูกใช้ด้วยมือที่สวมถุงมือ
 */

import type { ReactNode } from 'react';

import { describePendingSyncReason, type PendingSyncStatus } from './pendingSync';
import { primaryTouchTargetStyle, touchTargetStyle } from './touch';
import type { ScanSubmitState } from './useScanSubmit';

/**
 * สิ่งที่หน้าจอทั้งสามโหมดรับมาจาก shell เหมือนกัน
 *
 * สถานะคิวถูกส่งลงมาจาก shell ตัวเดียว ไม่ให้แต่ละหน้าจอไป subscribe เอง —
 * ตัวเลข "รอซิงค์" บนหัวจอกับในหน้าจอต้องเป็นค่าเดียวกันเสมอ ไม่ใช่สองค่าที่
 * อ่านคนละรอบแล้วบังเอิญตรงกัน
 */
export interface ScanScreenProps {
  readonly pendingSync: PendingSyncStatus;
}

export interface ScanFieldProps {
  readonly label: string;
  readonly name: string;
  readonly testId: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly hint?: string;
  readonly placeholder?: string;
  /** `decimal` สำหรับจำนวน, `numeric` สำหรับลำดับขั้นตอน — แป้นบนมือถือต่างกัน */
  readonly inputMode?: 'text' | 'decimal' | 'numeric';
}

/**
 * ช่องกรอกหนึ่งช่องของหน้างาน
 *
 * ทุกช่องเป็น `type="text"` แม้แต่ช่องจำนวน — `type="number"` ปล่อยให้เบราว์เซอร์
 * ตีความค่าเป็นตัวเลขแล้วคืนสตริงที่ถูกแปลงกลับ ซึ่งเป็นจุดที่ `NUMERIC(18,4)`
 * เสียความละเอียดได้เงียบ ๆ ค่าที่พิมพ์ต้องเดินทางถึงเซิร์ฟเวอร์เหมือนเดิม
 */
export function ScanField({
  label,
  name,
  testId,
  value,
  onChange,
  hint,
  placeholder,
  inputMode = 'text',
}: ScanFieldProps) {
  return (
    <label className="cap-scan-field">
      <span className="cap-scan-field__label">{label}</span>
      <input
        className="cap-scan-field__input"
        style={touchTargetStyle}
        type="text"
        inputMode={inputMode}
        name={name}
        data-testid={testId}
        value={value}
        autoComplete="off"
        onChange={(event) => {
          onChange(event.target.value);
        }}
        {...(placeholder === undefined ? {} : { placeholder })}
      />
      {hint === undefined ? null : <span className="cap-scan-field__hint">{hint}</span>}
    </label>
  );
}

export interface ScanCommandFormProps {
  readonly testId: string;
  readonly title: string;
  readonly hint: string;
  readonly submitLabel: string;
  readonly state: ScanSubmitState;
  readonly pendingSync: PendingSyncStatus;
  readonly onSubmit: () => void;
  readonly children: ReactNode;
}

export function ScanCommandForm({
  testId,
  title,
  hint,
  submitLabel,
  state,
  pendingSync,
  onSubmit,
  children,
}: ScanCommandFormProps) {
  const queueUnavailable = pendingSync.availability === 'UNAVAILABLE';

  return (
    <form
      className="cap-scan-form"
      data-testid={testId}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <h2 className="cap-scan-form__title">{title}</h2>
      <p className="cap-scanner__note">{hint}</p>

      <div className="cap-scan-form__fields">{children}</div>

      {/*
        เตือนก่อนกด ไม่ใช่หลังกด — แต่ยังไม่ปิดปุ่ม เพราะสถานะคิวอาจกลับมาใช้ได้
        เองระหว่างนั้น (แท็บที่ค้างอยู่ถูกปิด, พื้นที่ถูกคืน) การกดแล้วได้คำตอบจริง
        ดีกว่าปุ่มที่กดไม่ได้โดยไม่มีทางรู้ว่ายังไม่ได้เลยหรือไม่
      */}
      {queueUnavailable ? (
        <p className="cap-scan-form__alert" data-testid={`${testId}-queue-down`} role="alert">
          คิวในเครื่องใช้งานไม่ได้ — {describePendingSyncReason(pendingSync.reason)}{' '}
          คำสั่งที่กดตอนนี้อาจเก็บลงเครื่องไม่ได้
        </p>
      ) : null}

      {state.status === 'invalid' ? (
        <div className="cap-scan-form__alert" data-testid={`${testId}-invalid`} role="alert">
          <strong>ยังเข้าคิวไม่ได้</strong>
          <ul className="cap-scan-form__problems">
            {state.problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
          <span className="cap-scan-form__note">
            ยังไม่มีคำสั่งถูกเก็บลงเครื่อง — แก้ค่าแล้วกดใหม่ได้เลย
          </span>
        </div>
      ) : null}

      {state.status === 'failed' ? (
        <p className="cap-scan-form__alert" data-testid={`${testId}-failed`} role="alert">
          เก็บคำสั่งลงเครื่องไม่สำเร็จ — {describePendingSyncReason(state.reason)}{' '}
          คำสั่งนี้ยังไม่มีอยู่ที่ใดเลย ทั้งในเครื่องและบนเซิร์ฟเวอร์ ต้องทำใหม่หลังแก้ปัญหาแล้ว
        </p>
      ) : null}

      {/*
        ข้อความหลังเข้าคิวสำเร็จคือประโยคที่ห้ามเพี้ยนที่สุดของหน้าจอนี้:
        ผู้ปฏิบัติงานต้องไม่เดินจากไปโดยเข้าใจว่าเซิร์ฟเวอร์รับงานไปแล้ว
      */}
      {state.status === 'queued' ? (
        <div className="cap-scan-form__queued" data-testid={`${testId}-queued`} role="status">
          <strong>เข้าคิวแล้ว — ยังไม่ส่งถึงเซิร์ฟเวอร์</strong>
          <span className="cap-scan-form__note">
            ลำดับที่ {state.record.device_sequence_no} ของเครื่องนี้ · สถานะ{' '}
            {state.record.sync_status}
            {pendingSync.availability === 'READY' ? ` · รอซิงค์ ${pendingSync.count} รายการ` : ''}
          </span>
          <span className="cap-scan-form__note">
            เซิร์ฟเวอร์จะเป็นผู้ตัดสินผลจริงเมื่อคำสั่งถูกซิงค์ขึ้นไป — บิลด์นี้ยังไม่มีตัวส่ง
          </span>
        </div>
      ) : null}

      <button
        type="submit"
        className="cap-scanner__cta"
        style={primaryTouchTargetStyle}
        data-testid={`${testId}-submit`}
        disabled={state.status === 'queuing'}
      >
        {state.status === 'queuing' ? 'กำลังเก็บลงเครื่อง…' : submitLabel}
      </button>
    </form>
  );
}
